import { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Form, Button, Spinner, Badge, Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import Template from "../../template";

import { useAuth } from "../../context/auth";
import { useCorrecao } from "../../context/correcao";

import * as S from "./styled";

const PAID_PLANS = new Set(["ESSENCIAL", "PLUS", "VIP", "PREMIUM"]);
const TOAST_NO_THEMES = "no-themes-available";
const TOAST_THEMES_ERROR = "themes-load-error";

function formatApiError(error) {
  const msg = error?.response?.data?.message || error?.message || "Erro inesperado";

  if (typeof msg === "string") {
    const lower = msg.toLowerCase();
    if (lower.includes("atingiu limite")) return msg;
    if (lower.includes("plano")) return msg;
  }

  return "Não foi possível enviar sua redação. Tente novamente.";
}

/**
 * Parse super defensivo para não derrubar a tela.
 * - aceita null/undefined
 * - lida com "undefined", "null", string vazia
 * - try/catch
 */
function safeParseUser(raw) {
  try {
    if (!raw) return null;
    if (raw === "undefined" || raw === "null") return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function UploadIcon() {
  // Ícone simples inline (sem dependências)
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 16V8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 11.5L12 8l3.5 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 16.5c0 2.485-2.239 4.5-5 4.5H9c-2.761 0-5-2.015-5-4.5S6.239 12 9 12c.33 0 .655.03.97.088C10.78 10.31 12.264 9 14 9c2.21 0 4 2.015 4 4.5c1.105 0 2 .895 2 3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.55"
      />
    </svg>
  );
}

function EnviarRedacaoIA() {
  const history = useHistory();
  const fileInputRef = useRef(null);

  // ✅ fonte principal: AuthContext
  const auth = useAuth?.();
  const userFromContext = auth?.user;

  // ✅ fallback defensivo: localStorage (NUNCA derruba)
  const userFromStorage = useMemo(() => {
    try {
      return safeParseUser(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);

  const user = userFromContext && Object.keys(userFromContext).length ? userFromContext : userFromStorage;
  const plan = user?.plano || user?.plan;

  // useCorrecao: pode não existir provider dependendo do layout.
  // Vamos proteger com try/catch pra evitar tela branca.
  let saveCorrecaoCompleta;
  let clearCorrecaoData;

  try {
    const correcao = useCorrecao?.();
    saveCorrecaoCompleta = correcao?.saveCorrecaoCompleta;
    clearCorrecaoData = correcao?.clearCorrecaoData;
  } catch (e) {
    // Se não tiver provider, não quebra a página.
    saveCorrecaoCompleta = undefined;
    clearCorrecaoData = undefined;
  }

  const isTeste = plan === "TESTE";
  const isPaid = PAID_PLANS.has(plan);

  const [temas, setTemas] = useState([]);
  const [loadingTemas, setLoadingTemas] = useState(false);

  const [temaLivre, setTemaLivre] = useState("");
  const [temaSelecionadoId, setTemaSelecionadoId] = useState("");

  const selectedTema = useMemo(
    () => temas.find((t) => String(t.id) === String(temaSelecionadoId)),
    [temas, temaSelecionadoId]
  );

  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [arquivoNome, setArquivoNome] = useState("");
  const [previewImagem, setPreviewImagem] = useState(null);

  const [loading, setLoading] = useState(false);
  const [correcao, setCorrecao] = useState(null);

  // Carregar temas somente para planos pagos
  useEffect(() => {
    let alive = true;

    async function fetchTemas() {
      if (!isPaid) return;

      try {
        setLoadingTemas(true);
        const res = await api.get("/temas/disponiveis");
        if (!alive) return;

        const list = Array.isArray(res.data) ? res.data : [];
        setTemas(list);

        if (list.length === 0) {
          if (!toast.isActive(TOAST_NO_THEMES)) {
            toast.info("Nenhum tema disponível no momento.", { toastId: TOAST_NO_THEMES });
          }
        } else {
          // Se antes avisou "nenhum tema", e agora veio tema, você pode limpar esse toast:
          toast.dismiss(TOAST_NO_THEMES);
        }
      } catch (err) {
        console.error(err);
        if (!toast.isActive(TOAST_THEMES_ERROR)) {
        toast.error("Erro ao carregar temas disponíveis.", { toastId: TOAST_THEMES_ERROR });
      }
      } finally {
        if (alive) setLoadingTemas(false);
      }
    }

    fetchTemas();
    return () => {
      alive = false;
    };
  }, [isPaid]);

  const generatePreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImagem(e.target.result);
    reader.readAsDataURL(file);
  };

  const validateFile = (file) => {
    if (!file) return "Selecione uma imagem (JPG ou PNG).";
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) return "Envie apenas JPG ou PNG.";
    if (file.size > 10 * 1024 * 1024) return "Arquivo muito grande. Máximo 10MB.";
    return null;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const err = validateFile(file);
    if (err) {
      toast.error(err);
      event.target.value = "";
      return;
    }

    setArquivoSelecionado(file);
    setArquivoNome(file.name);
    generatePreview(file);
  };

  const handleClickUpload = () => fileInputRef.current?.click();

  const getThemePayload = () => {
    if (isTeste) {
      return { theme: temaLivre.trim(), temaId: null };
    }

    // pago
    const theme = selectedTema
      ? `${selectedTema.genero ?? ""} ${selectedTema.aula ?? ""} - ${selectedTema.nome ?? ""}`
          .replace(/\s+/g, " ")
          .trim()
      : "";

    return { theme, temaId: temaSelecionadoId };
  };

  const canSubmit = useMemo(() => {
    if (!plan) return false;
    if (!isTeste && !isPaid) return false;

    const { theme, temaId } = getThemePayload();
    if (!theme) return false;

    if (isPaid && !temaId) return false;
    if (!arquivoSelecionado) return false;

    return true;
  }, [plan, isTeste, isPaid, temaLivre, temaSelecionadoId, arquivoSelecionado, temas]);

  const submit = async () => {
    if (!plan) {
      toast.error("Plano do usuário não identificado.");
      return;
    }

    if (!isTeste && !isPaid) {
      toast.error("Seu plano não permite envio por IA.");
      return;
    }

    const fileErr = validateFile(arquivoSelecionado);
    if (fileErr) {
      toast.error(fileErr);
      return;
    }

    const { theme, temaId } = getThemePayload();
    if (!theme) {
      toast.error("Informe o tema da redação.");
      return;
    }

    if (isPaid && !temaId) {
      toast.error("Selecione um tema.");
      return;
    }

    setLoading(true);
    setCorrecao(null);

    try {
      clearCorrecaoData?.();

      const data = new FormData();
      data.append("file", arquivoSelecionado);
      data.append("theme", theme);

      if (isPaid) {
        // backend espera "tema" (id do tema)
        data.append("tema", String(temaId));
      }

      const endpoint = isTeste ? "/redacao/ia/teste" : "/redacao/ia";
      const retorno = await api.post(endpoint, data);

      console.log("RETORNO /redacao/ia:", retorno?.data);

      toast.success("Correção gerada com sucesso!");

      const payload = retorno?.data || null;

      // ✅ TESTE (mantém como está hoje)
      if (isTeste && payload) {
        const imageDataURL = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(arquivoSelecionado);
        });

        saveCorrecaoCompleta?.({
          correcao: payload,
          imagem: imageDataURL,
          arquivo: arquivoSelecionado,
          tema: theme,
        });

        history.push("/correcao-teste/resultado");
        return;
      }

      // ✅ PAGO: NÃO tem id → usa o contexto e vai pra /correcao-ia/resultado
      if (isPaid && payload) {
        const imageDataURL = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(arquivoSelecionado);
        });

        saveCorrecaoCompleta?.({
          correcao: payload,
          imagem: imageDataURL,
          arquivo: arquivoSelecionado,
          tema: theme,
        });

        history.push("/correcao-ia/resultado");
        return;
      }

      // fallback
      setCorrecao(payload);
    } catch (error) {
      console.error(error);
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const PLAN_LABEL = {
    ESSENCIAL: "ESSENCIAL",
    PLUS: "PLUS",
    VIP: "VIP",
    PREMIUM: "PREMIUM",
    TESTE: "TESTE",
  };

  const normalizedPlan = String(plan || "").trim().toUpperCase();
  const planLabel = PLAN_LABEL[normalizedPlan] || normalizedPlan || "PLANO";

  // react-bootstrap 1.5 + bootstrap4: use variant=
  const badge = isTeste ? (
    <Badge className="plan-badge" variant="warning">
      TESTE
    </Badge>
  ) : isPaid ? (
    <Badge className="plan-badge" variant="primary">
      {planLabel}
    </Badge>
  ) : (
    <Badge className="plan-badge" variant="secondary">
      SEM IA
    </Badge>
  );

  const selectHasValue = Boolean(temaSelecionadoId);

  return (
    <Template>
      <S.MainWrapper>
        <Container>
          <Row>
            <Col sm={12}>
              <S.MainContent>
                {/* Top area (pronto pra alert local no futuro) */}
                <S.TopMessageArea>
                  {/* Exemplo futuro (não implementado): <S.TopAlert variant="danger">Mensagem...</S.TopAlert> */}
                </S.TopMessageArea>

                <S.PageHeader>
                  <S.HeaderText>
                    <S.TitleRow>
                      <S.Title>Enviar redação para correção por IA</S.Title>
                      {badge}
                    </S.TitleRow>

                    <S.Subtitle>
                      Envie sua redação em imagem (JPG/PNG). Você receberá uma correção automática com notas por competência e feedback.
                    </S.Subtitle>
                  </S.HeaderText>
                </S.PageHeader>

                {!plan && (
                  <Alert variant="danger" className="mt-3">
                    Não foi possível identificar o usuário/plano. Faça login novamente.
                  </Alert>
                )}

                {/* Card 1: Dados da redação */}
                <S.SectionCard>
                  <S.SectionHeader>
                    <div>
                      <S.SectionTitle>Dados da redação</S.SectionTitle>
                      <S.SectionHint>Escolha um tema para direcionar a correção.</S.SectionHint>
                    </div>
                  </S.SectionHeader>

                  {isTeste && (
                    <Form.Group className="mb-2">
                      <S.FieldLabel>Tema da redação <span className="required">*</span></S.FieldLabel>
                      <Form.Control
                        type="text"
                        placeholder="Digite o tema (até 200 caracteres)"
                        value={temaLivre}
                        onChange={(e) => setTemaLivre(e.target.value)}
                        maxLength={200}
                      />
                      <S.HelpText>
                        No plano TESTE, o tema é livre e a correção não aparece em “Minhas redações”.
                      </S.HelpText>
                    </Form.Group>
                  )}

                  {isPaid && (
                    <Form.Group className="mb-2">
                      <S.FieldLabel>Selecione o tema <span className="required">*</span></S.FieldLabel>

                      <Form.Control
                        as="select"
                        value={temaSelecionadoId}
                        onChange={(e) => setTemaSelecionadoId(e.target.value)}
                        disabled={loadingTemas}
                        className={selectHasValue ? "has-value" : "is-placeholder"}
                      >
                        <option value="">{loadingTemas ? "Carregando temas..." : "Selecione um tema"}</option>
                        {temas.map((t) => (
                          <option key={t.id} value={t.id}>
                            {`${t.genero ?? ""} ${t.aula ?? ""} - ${t.nome ?? ""}`.replace(/\s+/g, " ").trim()}
                          </option>
                        ))}
                      </Form.Control>

                      <S.HelpText>Os temas são carregados automaticamente conforme seu plano.</S.HelpText>

                      {/* Pronto para erro visual (sem alterar lógica):
                        <S.FieldError>Selecione um tema para continuar.</S.FieldError>
                      */}
                    </Form.Group>
                  )}

                  {!isTeste && !isPaid && plan && (
                    <Alert variant="secondary" className="mt-2 mb-0">
                      Seu plano atual não possui envio de redação por IA.
                    </Alert>
                  )}
                </S.SectionCard>

                {/* Card 2: Upload */}
                <S.SectionCard>
                  <S.SectionHeader>
                    <div>
                      <S.SectionTitle>Upload da redação</S.SectionTitle>
                      <S.SectionHint>Envie uma imagem legível para melhor resultado.</S.SectionHint>
                    </div>
                  </S.SectionHeader>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />

                  <S.Dropzone
                    onClick={handleClickUpload}
                    role="button"
                    aria-label="Selecionar arquivo"
                    data-hasfile={arquivoNome ? "true" : "false"}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") handleClickUpload();
                    }}
                  >
                    <S.DropzoneInner>
                      <S.DropIcon>
                        <UploadIcon />
                      </S.DropIcon>

                      <div>
                        <S.DropTitle>Clique para selecionar sua imagem</S.DropTitle>
                        <S.DropSub>Apenas JPG/PNG • Máximo 10MB</S.DropSub>

                        {arquivoNome ? (
                          <S.FileRow>
                            <S.FileCenter>
                              <S.FileChip title={arquivoNome} className="stack">
                                <span className="label">Arquivo selecionado</span>
                                <span className="name">{arquivoNome}</span>
                              </S.FileChip>
                            </S.FileCenter>
                          </S.FileRow>
                        ) : (
                          <S.DropMuted>Nenhum arquivo selecionado</S.DropMuted>
                        )}
                      </div>
                    </S.DropzoneInner>
                  </S.Dropzone>

                  {previewImagem && (
                    <S.PreviewWrap>
                      <S.PreviewTitle>Preview</S.PreviewTitle>
                      <S.PreviewBox>
                        <img src={previewImagem} alt="Preview da redação" />
                        <S.PreviewMeta title={arquivoNome}>{arquivoNome}</S.PreviewMeta>
                      </S.PreviewBox>
                    </S.PreviewWrap>
                  )}

                  <S.Actions>
                    <Button
                      variant="primary"
                      disabled={!canSubmit || loading || loadingTemas}
                      onClick={submit}
                      className="px-5"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="mr-2" />
                          Nossa IA está realizando a correção, aguarde...
                        </>
                      ) : (
                        "Enviar"
                      )}
                    </Button>

                    {!canSubmit && !loading && (
                      <S.HelperBelow>
                        {isPaid && !temaSelecionadoId
                          ? "Selecione um tema para continuar."
                          : !arquivoSelecionado
                          ? "Selecione uma imagem para continuar."
                          : "Preencha os dados para enviar."}
                      </S.HelperBelow>
                    )}
                  </S.Actions>
                </S.SectionCard>

                {/* Resultado inline para planos pagos */}
                {correcao && isPaid && (
                  <S.SectionCard>
                    <S.SectionHeader>
                      <div>
                        <S.SectionTitle>Resultado da correção</S.SectionTitle>
                        <S.SectionHint>Esta redação também ficará disponível em “Minhas redações”.</S.SectionHint>
                      </div>
                    </S.SectionHeader>

                    <Alert variant="success" className="mb-2">
                      Correção gerada com sucesso.
                    </Alert>

                    <div className="d-flex flex-wrap align-items-center">
                      <Badge variant="dark" className="mr-2 mb-2">
                        Nota final: {correcao?.final_score ?? correcao?.nota_final ?? "-"}
                      </Badge>
                      <Badge variant="info" className="mb-2">Tipo: IA</Badge>
                    </div>

                    <div className="mt-2 text-right">
                      <Button variant="outline-primary" onClick={() => history.push("/minhas-redacoes?tipo=ia")}>
                        Ir para Minhas redações
                      </Button>
                    </div>
                  </S.SectionCard>
                )}
              </S.MainContent>
            </Col>
          </Row>
        </Container>
      </S.MainWrapper>
    </Template>
  );
}

export default EnviarRedacaoIA;